import * as React from 'react'

import { Alert, Box, Text } from '@factorialco/deprecated-design-system'
import compact from 'lodash/compact'
import includes from 'lodash/includes'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import UserSelector from 'modules/core/components/UserSelector'
import createComputed from 'modules/core/hooks/createComputed'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import users, { employeeUser } from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import EsignatureRequester from 'modules/documents/components/BulkFiles/DocumentAssigner/EsignatureRequester'
import AssignConfirmationModal from 'modules/documents/components/BulkFiles/DocumentAssigner/MultiDocumentAssignerForm/AssignConfirmationModal'
import { Document as DocumentEntity } from 'modules/documents/hooks/documents/useGetDocuments'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { UploadProcess } from 'modules/documents/hooks/useGetUploadProcess'
import {
  UploadProcessDocument,
  useGetUploadProcessDocuments,
} from 'modules/documents/hooks/useGetUploadProcessDocuments'
import { useSendInBulkUpdate } from 'modules/documents/hooks/useSendInBulkUpdate'
import { isTemplate } from 'modules/documents/lib/Documents'
import { ESIGNATURE_MAX_FILE_SIZE } from 'modules/documents/lib/constants'
import { isPersonalFilesWorkflow } from 'modules/documents/lib/distributionWorkflows'
import { DistributionMethod } from 'modules/documents/lib/enums'
import type { AssignEsignatureForm } from 'modules/documents/lib/forms/AssignEsignatureForm'
import scores from 'modules/documents/store/collections/scores'
import type Score from 'modules/documents/store/models/Score'
import type { Employees } from 'modules/employees/store/collections/employees'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'

const SUGGESTED_SCORE_THRESHOLD = 10
const SELECTED_SCORE_THRESHOLD = 100
const MAX_DEFAULT_MATCHES = 1

type EsignatureRequesterProps = React.ComponentProps<typeof EsignatureRequester>

const assigningLabel = createComputed((selectedIds: number[]) => {
  const filteredUsers = compact(selectedIds.map((id) => users.get(id)))

  if (!filteredUsers || filteredUsers.length === 0) {
    return i18n.t('split_documents.assign_documents.select_employees')
  }

  return i18n.tp('split_documents.assign_documents.send_to', {
    count: filteredUsers.length,
    employee: filteredUsers.map((user) => user.attributes.first_name),
  })
})

const getSelectedEmployees = createComputed((selectedIds: number[]) => {
  const selectedUsers = compact(selectedIds.map((id) => users.get(id)))

  return compact(selectedUsers.map((user) => user.employee))
})

const sortEmployees = (employeesToSort: Employee[]) => {
  return sortBy(employeesToSort, (employee) => employeeUser(employee).fullName())
}

const getUsersToDisplay = createComputed(
  (
    employeesToDisplay: Employees,
    confirmedEmployees: Employee[],
    suggestedEmployees: Employee[]
  ) => {
    const userIds = [
      ...sortEmployees(confirmedEmployees),
      ...sortEmployees(suggestedEmployees),
      ...sortEmployees(employeesToDisplay.models),
    ].map((employee) => employee.attributes.access_id)

    const uniqueUserIds = Array.from(new Set(userIds))

    return compact(uniqueUserIds.map((id) => users.get(id)))
  }
)

const filterScores = (
  filteredEmployees: Employees,
  document: Document,
  scoreFn: (score: Score) => boolean
) => {
  const employeeIds = scores
    .filter((score) => score.attributes.document_id === document.id)
    .filter(scoreFn)
    .map((score: Score) => score.attributes.employee_id)

  return filteredEmployees.filter((employee) => includes(employeeIds, employee.attributes.id))
}

const getConfirmedEmployees = (filteredEmployees: Employees, document: Document) => {
  const confirmedEmployees = filterScores(
    filteredEmployees,
    document,
    (score: Score) => score.attributes.score >= SELECTED_SCORE_THRESHOLD
  )

  return sortEmployees(confirmedEmployees)
}

const getSuggestedEmployees = (filteredEmployees: Employees, document: Document) => {
  const suggestedEmployees = filterScores(
    filteredEmployees,
    document,
    (score: Score) =>
      score.attributes.score >= SUGGESTED_SCORE_THRESHOLD &&
      score.attributes.score < SELECTED_SCORE_THRESHOLD
  )

  return sortEmployees(suggestedEmployees)
}

interface Props {
  document: Document
  selectedIdsForEsignature: number[]
  onSelectIdsForEsignature: (arg0: number[]) => void
  requestEsignatureForm: AssignEsignatureForm
  uploadProcess: UploadProcess
}

const MultiDocumentAssignerForm = ({
  document,
  selectedIdsForEsignature,
  requestEsignatureForm,
  onSelectIdsForEsignature,
  uploadProcess,
}: Props) => {
  const [loadingScores, setLoadingScores] = React.useState(false)
  const [loadingEmployees, setLoadingEmployees] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [selectedIds, setSelectedIds] = React.useState<number[]>([])
  const [uploadProcessDocument, setUploadProcessDocument] = React.useState<
    UploadProcessDocument | undefined
  >()
  const assignLabel = assigningLabel(selectedIds).get()
  const selectedEmployees = getSelectedEmployees(selectedIds).get()
  const selectedSignees = getSelectedEmployees(selectedIdsForEsignature).get()
  const usersToDisplay = getUsersToDisplay(
    employees,
    getConfirmedEmployees(employees, document),
    getSuggestedEmployees(employees, document)
  ).get()
  const suggestedIds = isTemplate(document)
    ? []
    : getSuggestedEmployees(employees, document).map((employee) => employee.attributes.access_id)
  const confirmedIds = isTemplate(document)
    ? []
    : getConfirmedEmployees(employees, document).map((employee) => employee.attributes.access_id)

  const { loading: loadingUploadProcessDocuments, fetch: fetchUploadProcessDocuments } =
    useGetUploadProcessDocuments()

  const fetchCurrentUploadProcessDocument = useCallback(async () => {
    const currentUploadProcessDocument = await fetchUploadProcessDocuments({
      documentIds: [document.id],
    })
    return currentUploadProcessDocument?.[0]
  }, [document.id])

  const gatherUploadProcessDocuments = async () => {
    setUploadProcessDocument(await fetchCurrentUploadProcessDocument())
  }

  const { submit: onSubmitSendInBulk } = useSendInBulkUpdate()

  useEffect(() => {
    const gatherScores = async () => {
      setLoadingScores(true)
      await scores.fetch({
        data: {
          document_ids: [document.id],
        },
      })

      setSelectedIds([])
      const matchIds = [...getConfirmedEmployees(employees, document)].map(
        (employee) => employee.attributes.access_id
      )
      if (MAX_DEFAULT_MATCHES >= matchIds.length) {
        setSelectedIds(matchIds)
      }
      setLoadingScores(false)
    }

    if (isPersonalFileWorkflow) {
      gatherScores()
    }
    gatherUploadProcessDocuments()
  }, [document.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const onAssignDocument = async () => {
    const requestEsignature = uploadProcess.esignatureRequest ?? false
    setLoadingEmployees(true)
    app.setProgress({
      message: i18n.t('common.sending'),
    })

    if (requestEsignature && document.fileSize && document.fileSize > ESIGNATURE_MAX_FILE_SIZE) {
      setLoadingEmployees(false)
      app.setProgress(null)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('documents.beta.file_errors.max_file_size_esignature'),
      })
      return
    }

    if (!selectedEmployees || selectedEmployees.length === 0) {
      setLoadingEmployees(false)
      app.setProgress(null)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
      throw new Error("Can't assign document to null employee")
    }

    const esignatureRequest = uploadProcess.esignatureRequest ?? false
    const additionalSigners =
      (uploadProcessDocument?.additionalSigners &&
      uploadProcessDocument?.additionalSigners.length > 0
        ? uploadProcessDocument?.additionalSigners.map((signer) => signer.id)
        : uploadProcess?.additionalSigners.map((signer) => signer.id)) || []

    const result = await onSubmitSendInBulk({
      id: document.id,
      byPendingAssignment: true,
      distributionMethod: DistributionMethod.Bulk,
      employeeIds: selectedEmployees.map((employee) => employee.attributes.id),
      isPendingAssignment: false,
      public: true,
      requestEsignature: esignatureRequest,
      signeeIds: additionalSigners,
    })

    const handled = handleResult(result, () => undefined)

    const documentIds =
      handled.success && handled.data.documents?.map((doc: DocumentEntity) => doc.id)

    if (documentIds) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('split_documents.bulk_modal.loading.description'),
      })
    }

    setLoadingEmployees(false)
    app.setProgress(null)
  }

  const onConfirm = () => {
    setOpen(false)
    onAssignDocument()
  }

  const onClose = () => {
    setOpen(false)
  }

  const onSubmit: EsignatureRequesterProps['onClick'] = async () => {
    if (selectedIds.length <= 1 && !isTemplate(document)) {
      await onAssignDocument()
    } else {
      setOpen(true)
    }
  }

  const setAssignerId = (value: number | null | undefined) => {
    if (!value) return
    if (selectedIds.includes(value)) return setSelectedIds(selectedIds.filter((id) => id !== value))
    if (isPersonalFileWorkflow) return setSelectedIds([value])

    setSelectedIds([...selectedIds, value])
  }

  const isPersonalFileWorkflow = isPersonalFilesWorkflow(uploadProcess)
  const loading = loadingScores || loadingEmployees || loadingUploadProcessDocuments
  const isDisabled = !selectedIds || selectedIds.length === 0 || loading

  if (loading) return <Loading />

  return (
    <Box flexDirection="column" height="full">
      <Box flexGrow>
        <Box flexDirection="column" padding="s16" minHeight="s64" justifyContent="center">
          <Text.Normal weight="medium">
            {i18n.t('split_documents.assign_documents.recipients_description_v2')}
          </Text.Normal>
        </Box>
        <Box padding="s16" paddingTop="s0">
          <UserSelector
            users={usersToDisplay}
            selectedIds={selectedIds}
            onSelect={setAssignerId}
            {...(isPersonalFileWorkflow ? {} : { onSelectMultiple: setSelectedIds })}
            autoSort={false}
            suggestedUsers={suggestedIds}
            confirmedUsers={confirmedIds}
            size="full"
            applyDefaultFilters
          />
        </Box>
      </Box>
      <Box
        padding="s16"
        gap="s16"
        background="grey200"
        border={{
          top: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        style={{ bottom: '0', position: 'sticky' }}
      >
        {isPersonalFileWorkflow && (
          <Box gap="s16">
            <Alert color="warning">
              <Text.Normal color="grey1000">
                {i18n.t('split_documents.assign_documents.personal_files_alert')}
              </Text.Normal>
            </Alert>
          </Box>
        )}
        <EsignatureRequester
          selectedIdsForEsignature={selectedIdsForEsignature}
          recipientsCount={selectedIds.length}
          requestEsignatureForm={requestEsignatureForm}
          onSelectIdsForEsignature={onSelectIdsForEsignature}
          uploadProcess={uploadProcess}
          uploadProcessDocument={uploadProcessDocument}
          onSignersUpdateFinished={gatherUploadProcessDocuments}
          disabled={isDisabled}
          onClick={onSubmit}
          label={assignLabel}
        />
      </Box>
      {open && (
        <AssignConfirmationModal
          fileName={document.filename}
          employees={selectedEmployees}
          requestEsignature={requestEsignatureForm.get('requestEsignature').value}
          signees={selectedSignees}
          onClose={onClose}
          onConfirm={onConfirm}
          document={document}
        />
      )}
    </Box>
  )
}

export default observer(MultiDocumentAssignerForm)
